/**
 * Именованные константы доменной математики геометрического движка.
 * Заменяют магические числа в geometry.ts и calculator.ts,
 * делая нагрузочные значения видимыми для ревью и безопасными для изменений.
 *
 * Файл не имеет внутренних импортов — он является листовым узлом графа зависимостей.
 * Все потребители импортируют ИЗ этого файла; ничто сюда не импортируется.
 */

/**
 * Порог ширины поверхности для классификации как Площадь (m2) vs. Откосы (mp).
 * Принцип сметчика: поверхность ≥ 0.5 м шириной отделывается как площадь (m2);
 * более узкие поверхности (боковые стенки ниш < 0.5 м) считаются линейными откосами (mp).
 * Значение задано в метрах. Является нормой по практике ПП/ФЕР для отделочных работ.
 * КРИТИЧЕСКИ ВАЖНО: изменение этого значения меняет классификацию поверхностей в сметах.
 */
export const NICHE_SLOPE_THRESHOLD_M = 0.5;

/**
 * Толщина перегородки по умолчанию (в сантиметрах), если пользователь не задал явно.
 * Соответствует стандартной однослойной гипсокартонной перегородке (профиль 50 мм + два листа ГКЛ 12.5 мм).
 * Используется в расчёте периметра и площади перегородки при parseSafeNumber(p.thickness, DEFAULT_PARTITION_THICKNESS_CM).
 */
export const DEFAULT_PARTITION_THICKNESS_CM = 10;

/**
 * Коэффициент перевода сантиметров в метры (делитель).
 * Используется везде, где входные данные задаются в сантиметрах
 * (OpeningsManager, PartitionsManager), а геометрический движок работает в метрах.
 * Пример: parseSafeNumber(p.thickness, DEFAULT_PARTITION_THICKNESS_CM) / CM_TO_M.
 */
export const CM_TO_M = 100;

/**
 * Количество сторон не-колонной перегородки, участвующих в расчёте площади отделки.
 * Стандартная перегородка оштукатуривается (шпаклюется/красится) с двух сторон.
 * Колонны (ends === COLUMN_ENDS_COUNT) используют иную формулу периметра.
 */
export const PARTITION_SIDES = 2;

/**
 * Значение поля `ends`, обозначающее колонну в модели данных перегородки.
 * Колонна имеет 4 открытых грани: периметр = длина × 4, площадь = периметр × высота.
 * Перегородки с ends === COLUMN_ENDS_COUNT обрабатываются по колонной ветке расчёта.
 */
export const COLUMN_ENDS_COUNT = 4;

/**
 * Мультипликатор корректировки количества по умолчанию.
 * Если поле adjustment отсутствует, нечитаемо или не распознано — умножать qty на 1 (без изменений).
 * Используется как значение fallback в parseSafeNumber(adjStr.replace(...), DEFAULT_ADJUSTMENT_MULTIPLIER).
 */
export const DEFAULT_ADJUSTMENT_MULTIPLIER = 1;

/**
 * Коэффициент рецептного слоя по умолчанию.
 * Значение 1.0 означает применение услуги в полном объёме (100% нормы).
 * Используется, если layer.coefficient не является числом.
 */
export const DEFAULT_RECIPE_COEFFICIENT = 1;

/**
 * Делитель для перевода процентов в десятичную долю.
 * Используется при вычислении коэффициента запаса материала:
 * marginFactor = 1 + safetyMargin / PERCENT_DIVISOR.
 * Пример: safetyMargin = 10 → marginFactor = 1.10 (наценка 10%).
 */
export const PERCENT_DIVISOR = 100;
